import type { OfimaConsecutivos } from '../types/order'

export type PedidoConsultaRemision = {
  Numero: string
  NumeroOfima?: string | number
  Mensaje?: string
}

export type OrderLikeForB2b = {
  id: number
  orders_id: string
  store_name?: string
  id_remision?: string
  ofima_consecutivos?: OfimaConsecutivos | null
  extraDetails?: { tipo_cliente?: string }
  tipo_cliente?: string
}

/** FS en payload puede venir como `fs` o `FS` según capa del API */
export function getOfimaFsValue(c: OfimaConsecutivos | null | undefined | Record<string, unknown>): string {
  if (!c || typeof c !== 'object') return ''
  const o = c as Record<string, unknown>
  const v = o.fs ?? o.FS
  return v != null ? String(v).trim() : ''
}

export function getTipoClienteFromOrder(order: OrderLikeForB2b): string {
  const fromExtra = order.extraDetails?.tipo_cliente
  if (fromExtra != null && String(fromExtra).trim() !== '') {
    return String(fromExtra).trim()
  }
  if (order.tipo_cliente != null && String(order.tipo_cliente).trim() !== '') {
    return String(order.tipo_cliente).trim()
  }
  return ''
}

export function isTipoClienteB2B(tipo: string): boolean {
  return tipo.trim().toUpperCase() === 'B2B'
}

/**
 * Tras ejecutar-y-consultar, fusiona `NumeroOfima` por id de pedido para detectar remisiones nuevas
 * antes de que el GET /list vuelva a cargar.
 */
export function enrichOrdersWithConsultaRemision<T extends OrderLikeForB2b>(
  orders: T[],
  pedidosConsulta: PedidoConsultaRemision[]
): T[] {
  const byNumero = new Map(pedidosConsulta.map((p) => [String(p.Numero), p]))
  return orders.map((order) => {
    const p = byNumero.get(String(order.id))
    const fromConsulta =
      p?.NumeroOfima != null && String(p.NumeroOfima).trim() !== ''
        ? String(p.NumeroOfima).trim()
        : ''
    const prev = order.id_remision != null ? String(order.id_remision).trim() : ''
    const id_remision = fromConsulta || prev
    return { ...order, id_remision }
  })
}

export function orderNeedsFacturaFs(order: OrderLikeForB2b): boolean {
  const rem = order.id_remision != null ? String(order.id_remision).trim() : ''
  if (rem === '') return false
  const fs = getOfimaFsValue(order.ofima_consecutivos as Record<string, unknown>)
  return fs === ''
}

export function selectB2bOrdersPendingFactura<T extends OrderLikeForB2b>(orders: T[]): T[] {
  return orders.filter((o) => {
    if (!isTipoClienteB2B(getTipoClienteFromOrder(o))) return false
    if (!orderNeedsFacturaFs(o)) return false
    return true
  })
}

export function isRemisionYaFacturadaMessage(msg: string): boolean {
  const normalizedMsg = msg.toLowerCase()
  return (
    normalizedMsg.includes('ya tiene una factura asociada') ||
    normalizedMsg.includes('ya tiene factura asociada') ||
    normalizedMsg.includes('remision ya tiene factura') ||
    normalizedMsg.includes('la remisión ya tiene una factura')
  )
}
